import React from 'react';
import { Scale, AlertTriangle, CreditCard, Ban, FileWarning, Briefcase } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const TermsOfUse: React.FC = () => {
  const { i18n } = useTranslation();
  const isEng = i18n.language.startsWith('en');

  const content = {
    title: isEng ? "Terms of Use" : "Termos de Uso",
    subtitle: isEng
      ? "The rules and agreements for using MotoRush."
      : "As 'Regras do Jogo'. O contrato que estabelece os direitos e deveres entre a MotoRush, Motoristas, Passageiros e Clientes Corporativos.",
    sections: [
      {
        icon: <Ban className="text-red-500 w-6 h-6" />,
        title: isEng ? "Conduct and Suspensions" : "Regras de Conduta e Suspensão",
        desc: isEng
          ? "We reserve the right to ban users for inappropriate behavior, non-payment, or safety violations."
          : "A MotoRush reserva-se o direito de suspender ou banir permanentemente utilizadores da plataforma. Passageiros que não efectuem o pagamento devido no final da viagem ou demonstrem mau comportamento, bem como motoristas que apresentem conduta inadequada, perigosa no trânsito ou desrespeito aos padrões de qualidade da MotoRush, serão banidos."
      },
      {
        icon: <AlertTriangle className="text-orange-500 w-6 h-6" />,
        title: isEng ? "Liability Exemption" : "Isenção de Responsabilidade (Liability)",
        desc: isEng
          ? "MotoRush is a technological intermediary. We do not hold liability for traffic accidents, delivery delays due to Luanda's traffic, or package damages."
          : "A MotoRush actua estritamente como uma plataforma tecnológica intermediária. Não nos responsabilizamos por atrasos causados pelo trânsito imprevisível de Luanda. Em caso de acidentes de viação ou danos a pacotes transportados (especialmente para clientes B2B), a responsabilidade civil recai sobre o motorista prestador de serviço, embora a MotoRush colabore activamente com as autoridades e providencie os dados do seguro disponível."
      },
      {
        icon: <CreditCard className="text-green-500 w-6 h-6" />,
        title: isEng ? "Payments and Wallets" : "Política de Pagamentos e Carteiras (Wallets)",
        desc: isEng
          ? "Detailed rules on wallet mechanics, driver withdrawals, and corporate postpaid invoicing."
          : "O saldo nas carteiras virtuais (wallets) da App reflecte os valores ganhos ou depositados. O tempo de processamento para um motorista solicitar o levantamento para a sua conta bancária é de até 48 horas úteis (sujeito aos tempos do sistema bancário angolano). Para Clientes Corporativos (Corporate), o serviço funciona em regime de facturas pós-pagas a 30 dias, sujeitas a juros de mora em caso de incumprimento."
      },
      {
        icon: <FileWarning className="text-purple-500 w-6 h-6" />,
        title: isEng ? "Cancellation Fees" : "Taxas de Cancelamento",
        desc: isEng
          ? "If a driver arrives and the trip is cancelled, a fee applies."
          : "O tempo dos nossos parceiros é valioso. Se o motorista chegar ao local de recolha indicado e o passageiro cancelar a viagem após um tempo de espera superior a 5 minutos, ou se não comparecer, será aplicada uma taxa de cancelamento que será cobrada na próxima viagem ou deduzida da carteira."
      },
      {
        icon: <Briefcase className="text-blue-500 w-6 h-6" />,
        title: isEng ? "B2B / Corporate Terms" : "Termos Específicos para Contas Corporate (B2B)",
        desc: isEng
          ? "Corporate clients are bound by the signed SLA contract which supersedes these terms."
          : "As empresas parceiras devem aderir aos termos detalhados no Contrato de Nível de Serviço (SLA) assinado fisicamente, que detalha os níveis de serviço esperados. Em caso de conflito, o SLA sobrepõe-se a estes Termos de Uso genéricos da aplicação."
      }
    ]
  };

  return (
    <div className="pt-24 pb-16 bg-gray-50 min-h-screen animate-fadeIn">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-blue-100 rounded-2xl mb-4 text-blue-600">
            <Scale size={32} />
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">
            {content.title}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {content.subtitle}
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-12 space-y-10">
          {content.sections.map((section, idx) => (
            <div key={idx} className="flex flex-col md:flex-row gap-6 items-start group border-b border-gray-50 pb-8 last:border-0 last:pb-0">
              <div className="p-4 bg-gray-50 rounded-xl group-hover:scale-110 transition-transform shrink-0">
                {section.icon}
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{section.title}</h3>
                <p className="text-gray-600 leading-relaxed text-justify">
                  {section.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;
